import { useCallback, useEffect, useRef, useState } from 'react';
import { FiCheck, FiX } from 'react-icons/fi';
import { LuTicket } from 'react-icons/lu';
import {
  getEventCoverImage,
  useExternalTicketClick,
  useUpdateTicketPurchaseStatus,
  type EventItem,
  type TicketPurchaseStatus,
} from '../../api/events';
import { extractApiError, extractFieldErrors, hasErrorCode } from '../../utils/apiError';
import { toast } from '../../utils/toast';
import { cn } from '../../utils/cn';

const storageKey = (eventId: number | string) => `ticket_response:${eventId}`;

const readStored = (eventId: number | string): string | null => {
  try {
    return localStorage.getItem(storageKey(eventId));
  } catch {
    return null;
  }
};
const writeStored = (eventId: number | string, id: string | null) => {
  try {
    if (id) localStorage.setItem(storageKey(eventId), id);
    else localStorage.removeItem(storageKey(eventId));
  } catch {
    // storage unavailable — the prompt just won't reappear
  }
};

type Step = 'details' | 'purchase' | 'purchased' | 'not_purchased' | null;

/**
 * Get Tickets flow for externally ticketed events:
 * details modal → ticket-click API → open ticket site → on return ask "did you get your ticket?".
 */
export function useExternalTicketFlow(event: EventItem | undefined) {
  const eventId = event?.id;
  const [step, setStep] = useState<Step>(null);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [redirectFailed, setRedirectFailed] = useState(false);
  const clickMutation = useExternalTicketClick();
  const statusMutation = useUpdateTicketPurchaseStatus();
  // Set once the user has left the page after clicking; the prompt shows when they come back.
  const leftRef = useRef(false);

  const promptIfPending = useCallback(() => {
    if (eventId != null && readStored(eventId)) setStep((s) => (s === null ? 'purchase' : s));
  }, [eventId]);

  useEffect(() => {
    if (eventId == null) return;
    promptIfPending(); // returned in the same tab / reloaded
    const onLeave = () => {
      if (readStored(eventId)) leftRef.current = true;
    };
    const onReturn = () => {
      if (!leftRef.current) return;
      leftRef.current = false;
      promptIfPending();
    };
    const onVisibility = () => (document.hidden ? onLeave() : onReturn());
    window.addEventListener('blur', onLeave);
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('blur', onLeave);
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [eventId, promptIfPending]);

  const open = () => {
    setEmailError(null);
    setRedirectFailed(false);
    setRedirectUrl(null);
    setStep('details');
  };

  const openTicketSite = (url: string, tab?: Window | null) => {
    leftRef.current = true;
    try {
      if (tab && !tab.closed) {
        tab.location.href = url;
        return true;
      }
    } catch {
      // fall through to a fresh window
    }
    return Boolean(window.open(url, '_blank'));
  };

  const handleContinue = async () => {
    if (eventId == null) return;
    // Open the tab synchronously so popup blockers allow it, then point it at the URL.
    const tab = window.open('', '_blank');
    setEmailError(null);
    try {
      const result = await clickMutation.mutateAsync({
        eventId,
        payload: {
          ...(fullName.trim() && { full_name: fullName.trim() }),
          ...(email.trim() && { email: email.trim() }),
        },
      });
      writeStored(eventId, result.response_id);
      setRedirectUrl(result.url);
      setFullName('');
      setEmail('');
      if (openTicketSite(result.url, tab)) {
        setStep(null);
      } else {
        setRedirectFailed(true);
      }
    } catch (err) {
      tab?.close();
      const fieldErrors = extractFieldErrors(err);
      if (fieldErrors.email) setEmailError(fieldErrors.email);
      else if (hasErrorCode(err, 'EVENT_TICKET_URL_MISSING'))
        toast.error('Tickets are not available right now.');
      else toast.error(extractApiError(err, 'Failed to open the ticketing website'));
    }
  };

  const handleRetry = () => {
    if (redirectUrl && openTicketSite(redirectUrl)) setStep(null);
  };

  const answer = async (status: TicketPurchaseStatus) => {
    if (eventId == null) return;
    const responseId = readStored(eventId);
    if (!responseId) {
      setStep(null);
      return;
    }
    try {
      await statusMutation.mutateAsync({ eventId, responseId, status });
      writeStored(eventId, null);
      setStep(status);
    } catch (err) {
      if (hasErrorCode(err, 'EVENT_TICKET_RESPONSE_NOT_FOUND')) {
        writeStored(eventId, null);
        setStep(null);
      } else {
        toast.error(extractApiError(err, 'Could not save your response'));
      }
    }
  };

  const close = () => setStep(null);

  const modals = event ? (
    <>
      {step === 'details' && (
        <ModalShell
          onClose={close}
          className="max-w-[688px] sm:min-h-[351px] max-h-[calc(100dvh-2rem)] overflow-y-auto py-6 pl-6 rounded-2xl border-[#262626] shadow-[0_8px_32px_0_#00000073]"
        >
          <form
            className="flex items-stretch gap-6 sm:min-h-[303px]"
            onSubmit={(e) => {
              e.preventDefault();
              if (redirectFailed) handleRetry();
              else handleContinue();
            }}
          >
            <div className="relative hidden sm:block w-[40%] shrink-0 overflow-hidden rounded-lg">
              <img
                src={getEventCoverImage(event) || '/Musicade-Logo.png'}
                alt={event.event_title}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
            <div className="min-w-0 flex-1 pr-6">
              <h2 className="pr-10 font-['Funnel_Display'] font-semibold text-[20px] leading-none text-[#EAF2FD]">
                Before you continue
              </h2>
              <p className="mt-2 font-['Outfit'] text-[13px] leading-snug text-zinc-400">
                Share your name and email so the organizer who posted this event on Musicade can
                send you important updates, reminders, and announcements
              </p>

              <label className="mt-4 block font-['DM_Sans'] font-medium text-[13px] leading-[19.5px] text-[#C4C4C4]">
                Full name <span className="text-zinc-500">(Optional)</span>
                <input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="e.g. Alex Morgan"
                  maxLength={255}
                  className={fieldClass}
                />
              </label>

              <label className="mt-3 block font-['DM_Sans'] font-medium text-[13px] leading-[19.5px] text-[#C4C4C4]">
                Email address <span className="text-zinc-500">(Optional)</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@mail.com"
                  className={fieldClass}
                />
              </label>
              {emailError && <p className="mt-1 text-xs text-red-400">{emailError}</p>}
              {redirectFailed && (
                <p className="mt-3 text-xs text-red-400">
                  We couldn&apos;t redirect you to the ticketing website. Please try again.
                </p>
              )}

              <button
                type="submit"
                disabled={clickMutation.isPending}
                className="mt-4 w-full h-[39px] rounded-full bg-white px-7 py-3 text-black font-['Outfit'] font-semibold text-[12px] leading-none hover:bg-zinc-200 transition-colors cursor-pointer disabled:opacity-50 disabled:hover:bg-white disabled:cursor-not-allowed"
              >
                {clickMutation.isPending
                  ? 'Please wait…'
                  : redirectFailed
                    ? 'Retry'
                    : 'Continue to Ticket Website'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {step === 'purchase' && (
        <ModalShell onClose={close} className="max-w-[440px] p-8 text-center">
          <div className="flex flex-col items-center">
            <div className="mt-6 w-24 h-24 rounded-full bg-zinc-800 flex items-center justify-center">
              <LuTicket size={36} />
            </div>
            <h3 className="mt-6 font-['Funnel_Display'] font-medium text-[24px]">
              Did you get your ticket?
            </h3>
            <p className="mt-3 font-['Outfit'] text-[14px] text-zinc-300">
              Let us know if you completed your purchase so we can keep your event updates up to
              date
            </p>
            <button
              onClick={() => answer('purchased')}
              disabled={statusMutation.isPending}
              className="mt-6 w-full h-12 rounded-full bg-white text-[#212121] font-['Outfit'] font-semibold text-[14px] hover:bg-zinc-200 transition-colors disabled:opacity-60 cursor-pointer"
            >
              Yes, I bought a ticket
            </button>
            <button
              onClick={() => answer('not_purchased')}
              disabled={statusMutation.isPending}
              className="mt-3 w-full h-12 rounded-full border border-white/10 bg-zinc-900 font-['Outfit'] font-semibold text-[14px] hover:bg-white/10 transition-colors disabled:opacity-60 cursor-pointer"
            >
              Not yet
            </button>
            <p className="mt-5 pt-4 w-full border-t border-white/10 font-['Outfit'] text-[12px] text-zinc-500">
              Self-reported purchases aren&apos;t verified by Musicade.
            </p>
          </div>
        </ModalShell>
      )}

      {(step === 'purchased' || step === 'not_purchased') && (
        <ModalShell onClose={close} className="max-w-[440px] p-8 text-center">
          <div className="flex flex-col items-center">
            <div className="mt-6 w-24 h-24 rounded-full border border-green-900/60 bg-green-950/40 flex items-center justify-center">
              <div className="w-14 h-14 rounded-full bg-green-400 flex items-center justify-center">
                <FiCheck size={28} className="text-zinc-900" strokeWidth={3} />
              </div>
            </div>
            <h3 className="mt-6 font-['Funnel_Display'] font-medium text-[24px]">
              {step === 'purchased' ? 'Thanks for letting us know!' : 'No worries!'}
            </h3>
            <p className="mt-3 font-['Outfit'] text-[14px] text-zinc-300">
              {step === 'purchased'
                ? 'Thanks! We’ll update your event status and notify you about important changes'
                : 'You can always come back later. We’ll keep you updated if anything changes before the event'}
            </p>
            <button
              onClick={close}
              className="mt-6 w-full h-12 rounded-full bg-white text-[#212121] font-['Outfit'] font-semibold text-[14px] hover:bg-zinc-200 transition-colors cursor-pointer"
            >
              Got it
            </button>
          </div>
        </ModalShell>
      )}
    </>
  ) : null;

  return { open, modals };
}

const fieldClass =
  "mt-1.5 w-full h-[50px] rounded-[17.3px] border border-[#FFFFFF12] bg-[#0D0D0D] p-4 backdrop-blur-[28px] font-['DM_Sans'] text-[14px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30";

function ModalShell({
  children,
  onClose,
  className = '',
}: {
  children: React.ReactNode;
  onClose: () => void;
  className?: string;
}) {
  return (
    <div
      className="fixed inset-0 z-100 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'relative w-full rounded-3xl border border-white/10 bg-black text-white shadow-2xl',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-zinc-700 transition-colors cursor-pointer"
        >
          <FiX size={18} />
        </button>
        {children}
      </div>
    </div>
  );
}
